<template>
  <div class="container is-fluid" ref="container">
    <section class="section">
      <h1 class="title">{{ $t("bnpl:limit.title.list") }}</h1>

      <FieldListing
        v-if="loaded"
        :data="limits"
        :columns="tableColumns"
        :actions="actions"
        :searchable="true"
        :paginated="true"
        default-sort="created"
        store-fetch-command="limit/fetchList"
        store-get-command="limit/list"
        :search-fields="searchFields"
        ref="dataTable"
      >
        <b-button type="is-blue" @click="exportLimits()">{{ $t("mp:export.limits.button") }} </b-button>
      </FieldListing>

      <LoadingSkeleton v-else />
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { AUTHORITIES } from "@/utils/authorities";
import { BNPL_LIMIT_STATUS, SEARCH_FIELD_TYPES, BNPL_LIMIT_STATUS_TOKENS } from "@/utils/const";
import LoadingSkeleton from "@/components/LoadingSkeleton.vue";
import { formatDateTime, formatMoney, localizedFormatter } from "@/utils/util";
import exportMixin from "@/mixins/exportMixin";
import i18n from "@/plugins/i18n";

export default {
  name: "LimitList",
  title: "pp:page.bnpl_application_list.title",
  mixins: [exportMixin],
  components: {
    LoadingSkeleton,
    FieldListing: () => import("@/components/FieldListing.vue"),
  },
  computed: {
    ...mapGetters({
      fileGetter: "drawer/getFile",
      country: "i18n/country",
    }),
    canSeebnplApplicationsExport() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.BNPL.APPLICATION.ADMIN,
        AUTHORITIES.BNPL.APPLICATION.EXPORT,
      ]);
    },
  },
  data() {
    return {
      limits: [],
      BNPL_LIMIT_STATUS_TOKENS,
      searchFields: [
        {
          name: "bnpl:limit.list_header.id",
          identifier: "id",
          type: SEARCH_FIELD_TYPES.INTEGER,
          genericSearch: true,
        },
        {
          name: "bnpl:limit.list_header.first_name",
          identifier: "firstName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:limit.list_header.last_name",
          identifier: "lastName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:limit.list_header.phone",
          identifier: "phone",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:limit.list_header.email",
          identifier: "email",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:limit.list_header.expiration_date",
          identifier: "expirationDate",
          type: SEARCH_FIELD_TYPES.DATE,
          genericSearch: true,
        },
        {
          name: "bnpl:limit.list_header.created_at",
          identifier: "createdAt",
          type: SEARCH_FIELD_TYPES.DATE,
          genericSearch: true,
        },
        {
          name: "bnpl:limit.list_header.status",
          identifier: "status",
          values: BNPL_LIMIT_STATUS_TOKENS,
          type: SEARCH_FIELD_TYPES.ENUM,
          genericSearch: true,
        },
      ],
      tableColumns: [
        {
          field: "id",
          label: "bnpl:limit.list_header.id",
          sortable: false,
          format: (row) => {
            return row.id;
          },
        },
        {
          field: "phone",
          label: "bnpl:limit.list_header.phone",
          sortable: false,
          maxWidth: "100px",
          format: (row) => {
            return row.phone;
          },
        },
        {
          field: "createdAt",
          label: "bnpl:limit.list_header.created_at",
          sortable: false,
          format: (row) => {
            return localizedFormatter(this.country.languageIso).dateTime(row.createdAt);
          },
        },
        {
          field: "expirationDate",
          label: "bnpl:limit.list_header.expiration_date",
          sortable: false,
          format: (row) => {
            return localizedFormatter(this.country.languageIso).dateTime(row.expirationDate);
          },
        },
        {
          field: "limitValue",
          label: "bnpl:limit.header.limit_value",
          sortable: false,
          minWidth: "max-content",
          format: (row) => {
            // return row.limitValue;
            if (row.limitValue !== undefined) {
              return localizedFormatter(this.country.languageIso).money(row.currencyIso, row.limitValue);
            }
          },
        },
        {
          field: "status",
          label: "bnpl:limit.list_header.status",
          sortable: false,
          vhtml: true,
          format: (row) => {
            if ([BNPL_LIMIT_STATUS.ACTIVE].includes(row.status)) {
              return `<span class='has-text-success is-uppercase'>${i18n.t(BNPL_LIMIT_STATUS_TOKENS[row.status])}</span>`;
            } else if (
              [
                BNPL_LIMIT_STATUS.CANCELLED,
                BNPL_LIMIT_STATUS.DENIED,
                BNPL_LIMIT_STATUS.INTERRUPTED,
              ].includes(row.status)
            ) {
              return `<span class='has-text-danger is-uppercase'>${i18n.t(BNPL_LIMIT_STATUS_TOKENS[row.status])}</span>`;
            } else if ([BNPL_LIMIT_STATUS.EXPIRED].includes(row.status)) {
              return `<span class='has-text-warning-dark is-uppercase'>${i18n.t(BNPL_LIMIT_STATUS_TOKENS[row.status])}</span>`;
            } else {
              return `<span class='is-uppercase'>${i18n.t(BNPL_LIMIT_STATUS_TOKENS[row.status])}</span>`;
            }
          },
        },
      ],
      actions: [
        {
          icon: "chevron-right",
          iconPack: "fa",
          type: "is-grey",
          function: (row) => {
            this.openLimit(row.id);
          },
        },
      ],
      loaded: false,
    };
  },
  async created() {
    await this.fetchList();
  },
  methods: {
    ...mapActions({
      fetchFile: "drawer/download",
    }),
    async fetchList() {
      this.loaded = false;

      await this.$store.dispatch("limit/fetchList", "sort=created,desc");
      this.limits = this.$store.getters["limit/list"];

      console.log("Why is this not happening?");

      this.loaded = true;
      console.log(this.loaded);
    },
    openLimit(id) {
      this.$router.push({
        name: "Limit",
        params: {
          id: id,
        },
      });
    },
    getDate(date, part) {
      // example: 2021.06.15. 13:44 where part=1 is the time
      const d = formatDateTime(date);
      return d.split(" ")[part];
    },
    newDeal() {
      this.$router.push({
        name: "NewApplication",
      });
    },
    async exportLimits() {
      await this.downloadListExport(
        "container",
        "limit/exportLimits",
        "dataTable",
        `instacash-limits-export.json`
      );
    },
  },
};
</script>

<style scoped></style>
